import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  telegramService,
  telegramChatsQueryKey,
  telegramMessageQueryKey,
  telegramMessagesQueryKey,
  telegramStatusQueryKey,
} from "@/services/telegramService";
import type { TelegramAgentPermissions, TelegramListParams } from "@/types/telegram";
import { DEFAULT_TELEGRAM_AGENT_PERMISSIONS } from "@/types/telegram";

export function useTelegramConnection(userId: string | undefined) {
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: telegramStatusQueryKey(userId),
    queryFn: () => telegramService.getStatus(),
    enabled: !!userId,
    staleTime: 60_000,
  });

  const disconnectMutation = useMutation({
    mutationFn: () => telegramService.disconnect(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telegramStatusQueryKey(userId) });
      void queryClient.invalidateQueries({ queryKey: ["telegram-chats"] });
    },
  });

  const permissions = statusQuery.data?.agentPermissions ?? DEFAULT_TELEGRAM_AGENT_PERMISSIONS;

  return {
    connected: statusQuery.data?.connected ?? false,
    connectionLabel: statusQuery.data?.name || statusQuery.data?.phone,
    phone: statusQuery.data?.phone,
    name: statusQuery.data?.name,
    agentPermissions: permissions,
    isLoadingStatus: statusQuery.isLoading,
    refetchStatus: statusQuery.refetch,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    updatePermissions: async (next: Partial<TelegramAgentPermissions>) => {
      const result = await telegramService.updatePermissions(next);
      void queryClient.invalidateQueries({ queryKey: telegramStatusQueryKey(userId) });
      return result.agentPermissions;
    },
  };
}

export function useTelegramChats(userId: string | undefined, enabled: boolean, params?: TelegramListParams) {
  return useQuery({
    queryKey: telegramChatsQueryKey(userId, params),
    queryFn: () => telegramService.listChats(params),
    enabled: !!userId && enabled,
    staleTime: 2 * 60_000,
  });
}

export function useTelegramMessages(
  userId: string | undefined,
  chatId: string | null | undefined,
  enabled: boolean,
  params?: TelegramListParams
) {
  return useQuery({
    queryKey: telegramMessagesQueryKey(userId, chatId, params),
    queryFn: () => telegramService.listMessages(chatId!, params),
    enabled: !!userId && !!chatId && enabled,
    staleTime: 60_000,
  });
}

export function useTelegramMessage(
  chatId: string | undefined,
  messageId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: telegramMessageQueryKey(chatId, messageId),
    queryFn: () => telegramService.getMessage(chatId!, messageId!),
    enabled: !!chatId && !!messageId && enabled,
  });
}

export function useSendTelegramMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, text }: { chatId: string; text: string }) =>
      telegramService.sendMessage(chatId, text),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["telegram-messages", undefined, variables.chatId],
      });
      void queryClient.invalidateQueries({ queryKey: ["telegram-chats"] });
    },
  });
}
